import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { generateId } from '@repo/toolkit/generate-id'
import { transformColorFromTailwindColorsToRGB } from '@repo/toolkit/transform-colors'
import { fr } from 'date-fns/locale'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useHover } from 'usehooks-ts'

import useTranslate from '../../../../../apps/backoffice/src/lib/hooks/use-translate'
import { CalendarOneIcon } from '../../../icons'
import InputWrapper from '../input/input-wrapper'
import Typography from '../typography'
import { Calendar } from './calendar'
import { calendarFormatters } from './utils'

interface DateRangeInputProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: string
  value?: DateRange
  onDateRangeChange?: (range: DateRange | undefined) => void
  onClearDate?: () => void
}

export function DateRangeInput({
  className,
  value,
  label = 'sélectionner une date',
  onDateRangeChange,
  onClearDate
}: DateRangeInputProps) {
  const id = React.useMemo(() => generateId(), [])
  const { getLocale } = useTranslate()
  const locale = getLocale()
  const [dateRange, setDateRange] = useState<DateRange | undefined>(value)
  const [hoverRange, setHoverRange] = useState<DateRange | undefined>(dateRange)
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isHovering = useHover(buttonRef)

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range)
    onDateRangeChange?.(range)
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const element = document.getElementById(id)
      if (
        element &&
        !element.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      )
        setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: { code: string }) => {
      if (e.code === 'Tab') setOpen(false)
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    setHoverRange(dateRange)
  }, [dateRange])

  useEffect(() => {
    setDateRange(value)
  }, [value])

  const start = dateRange?.from ? DateFormatter.DDMMYYYY(dateRange.from) : '-'
  const end = dateRange?.to ? DateFormatter.DDMMYYYY(dateRange.to) : '-'

  return (
    <div id={id} className={cn('relative w-full', className)}>
      <InputWrapper
        icon={CalendarOneIcon}
        iconToTheRight={true}
        label={label}
        className="cursor-pointer"
        active={open || !!dateRange}
        focused={open}
        onClick={() => setOpen(!open)}
      >
        <button
          ref={buttonRef}
          type="button"
          className={cn('flex items-center justify-between outline-none')}
          onClick={() => setOpen(!open)}
          onFocus={() => {
            if (!isHovering) setOpen(true)
          }}
        >
          <Typography variant="label-medium">
            {dateRange?.from && dateRange?.to
              ? start === end
                ? `${start}`
                : `${start} - ${end}`
              : '-'}
          </Typography>
        </button>
      </InputWrapper>

      {open && (
        <div className="bg-white w-full absolute top-12 overflow-hidden rounded-lg drop-shadow-2xl z-10">
          <Typography
            variant="small"
            className="px-5 pt-3 flex justify-end cursor-pointer underline "
            color={tailwindColors.primary.DEFAULT}
            onClick={() => {
              setDateRange(undefined)
              onClearDate?.()
            }}
          >
            Effcer la date
          </Typography>

          <Calendar
            mode="range"
            locale={fr}
            numberOfMonths={2}
            selected={hoverRange}
            onDayMouseEnter={(day) => {
              if (dateRange?.from && !dateRange?.to) {
                // Check if the day is before the start date
                if (day < dateRange.from) setHoverRange({ from: day, to: dateRange.from })
                else setHoverRange({ from: dateRange.from, to: day })
              }
            }}
            onDayClick={(day, _modifiers, e) => {
              // A day picked with the mouse must not keep the focus, so Enter can submit the surrounding form or drawer
              if (e.detail > 0 && e.currentTarget instanceof HTMLElement) e.currentTarget.blur()
              if (dateRange?.from && dateRange?.to)
                handleDateRangeChange({ from: day, to: undefined })
              else if (dateRange?.from && !dateRange?.to) {
                if (day < dateRange.from) handleDateRangeChange({ from: day, to: dateRange.from })
                else handleDateRangeChange({ from: dateRange.from, to: day })
              } else handleDateRangeChange({ from: day, to: undefined })
            }}
            modifiersStyles={{
              range_middle: {
                backgroundColor: transformColorFromTailwindColorsToRGB(
                  tailwindColors.primary.light.DEFAULT
                ),
                color: transformColorFromTailwindColorsToRGB(tailwindColors.primary.DEFAULT)
              },
              outside: {
                color: tailwindColors.grey.light.active
              },
              range_start: {
                borderTopLeftRadius: '0.375rem',
                borderBottomLeftRadius: '0.375rem',
                color: tailwindColors.white.DEFAULT,
                backgroundColor: tailwindColors.primary.DEFAULT
              },
              range_end: {
                borderTopRightRadius: '0.375rem',
                borderBottomRightRadius: '0.375rem',
                backgroundColor: tailwindColors.primary.DEFAULT,
                color: tailwindColors.white.DEFAULT
              }
            }}
            formatters={calendarFormatters(locale)}
          />
        </div>
      )}
    </div>
  )
}
